<template>
    <v-app>
        <driverBar :plate="plate" back="driverDay"></driverBar>
        <v-main>
            <v-container v-if="stop">
                <div class="title black--text">{{ stop.custName || '(ไม่ระบุชื่อ)' }}</div>
                <div class="body-2 grey--text text--darken-1 mb-3">
                    จุดที่ {{ stop.seq }} · นัด {{ stop.promisedTime || '-' }}
                </div>

                <v-card class="mb-3"><v-card-text>
                    <div class="mb-2">{{ stop.custAddress }}</div>
                    <!-- จุดสังเกต, when staff took one. Free text a person wrote
                         for another person, so it is shown rather than resolved.
                         It sits under the address because that is where a driver
                         looks when Google has put them on the wrong ซอย, and it
                         is filled in more often than the พิกัด is. -->
                    <div v-if="stop.landmark" class="mb-2 body-2 blue-grey--text text--darken-2">
                        <v-icon small color="blue-grey darken-2" class="mr-1">mdi-map-marker-radius</v-icon>
                        จุดสังเกต: {{ stop.landmark }}
                    </div>
                    <v-btn v-if="stop.custTel" text color="primary" :href="'tel:' + stop.custTel">
                        <v-icon left>mdi-phone</v-icon>{{ stop.custTel }}
                    </v-btn>
                    <div class="body-2 grey--text text--darken-1 mt-2">
                        {{ stop.cardName }} · {{ stop.lines.length }} รายการ ·
                        {{ stop.returnOnly ? 'รับคืนราว' : 'ติดตั้งราว' }} {{ stop.installMinutes }} นาที
                    </div>
                    <div class="body-2 grey--text text--darken-1">
                        {{ stop.lines.map(lineRef).join(', ') }}
                    </div>
                </v-card-text></v-card>

                <!-- What this customer should receive, per SO, so the driver can
                     check it against what is on the truck before carrying it in.
                     A copy of SAP's lines taken when the job was created, hence
                     the note: an SO edited since is not reflected here. -->
                <v-card v-if="productLines.length" class="mb-3"><v-card-text>
                    <div class="d-flex align-center mb-1">
                        <div class="subtitle-2 black--text">สินค้าที่ต้องส่ง</div>
                        <v-spacer></v-spacer>
                        <div v-if="productCount" class="body-2">รวม {{ productCount }} ชิ้น</div>
                    </div>
                    <div v-for="line in productLines" :key="line.deliveryListId" class="mb-2">
                        <div v-if="productLines.length > 1" class="caption grey--text text--darken-1">
                            {{ lineRef(line) }}
                        </div>
                        <div v-if="line.returns && line.returns.length && line.products.length" class="caption font-weight-bold">ส่ง</div>
                        <div v-for="(p, i) in line.products" :key="i" class="d-flex body-2">
                            <!-- The code first: drivers know the products by
                                 SAP code, not by their description. -->
                            <div class="pr-2">
                                <span v-if="p.code" class="font-weight-bold mr-1">{{ p.code }}</span>
                                <span v-if="p.name !== p.code">{{ p.name }}</span>
                            </div>
                            <v-spacer></v-spacer>
                            <div class="font-weight-bold text-no-wrap">× {{ p.qty }}</div>
                        </div>
                        <template v-if="line.returns && line.returns.length">
                            <div class="caption font-weight-bold mt-1">รับคืน (RT {{ line.rtNo }})</div>
                            <div v-for="(p, i) in line.returns" :key="'r' + i" class="d-flex body-2">
                                <span class="mr-2">{{ p.name }}</span><v-spacer /><span>× {{ p.qty }}</span>
                            </div>
                        </template>
                    </div>
                    <div class="caption grey--text">ตามข้อมูล ณ วันที่จัดรถ</div>
                </v-card-text></v-card>

                <!-- Two buttons, not one with :disabled. A v-btn given an href
                     renders an <a>, and `disabled` on an anchor is not honoured
                     by the browser: the tap still followed the (empty) link. The
                     v-else has no href at all, so it is a real disabled button. -->
                <v-btn v-if="navUrl" block large outlined color="primary"
                    :href="navUrl" target="_blank">
                    <v-icon left>mdi-navigation</v-icon>นำทาง
                </v-btn>
                <v-btn v-else block large outlined disabled>
                    <v-icon left>mdi-navigation</v-icon>นำทาง
                </v-btn>
                <!-- What the driver is being navigated TO. An exact pin was
                     dropped by staff who asked the customer; anything else is
                     Google's reading of a free-text address, which for a Thai
                     address can land on the wrong ซอย. The driver decides
                     whether to just drive or to phone first, and can only do
                     that if the screen says which one this is. -->
                <div class="body-2 mb-3 mt-1 text-center" :class="navBasisClass">
                    {{ navBasis }}
                </div>

                <v-card class="mb-3"><v-card-text>
                    <v-timeline dense>
                        <v-timeline-item v-for="s in steps" :key="s.key" small
                            :color="stamped(s.key) ? 'green' : 'grey lighten-1'">
                            <div class="d-flex">
                                <div>{{ s.label }}</div>
                                <v-spacer></v-spacer>
                                <div class="body-2 grey--text text--darken-1">{{ stampClock(s.key) }}</div>
                            </div>
                        </v-timeline-item>
                    </v-timeline>
                </v-card-text></v-card>

                <!-- The button and its hint are ONE branch of the v-if chain below.
                     Left as siblings, the v-else alerts chained onto the hint's
                     v-if instead of the button's, and งานนี้เสร็จแล้ว showed under
                     every stop that was not awaiting close -- including ones the
                     driver had not yet left for. -->
                <template v-if="next">
                    <v-btn block x-large color="orange accent-4" dark
                        :loading="busy" :disabled="busy" @click="stamp">
                        {{ nextLabel }}
                    </v-btn>
                    <!-- The customer has signed but the job is not closed: the driver
                         left page 3 with ภายหลัง, or the app closed. Said here, because
                         otherwise the stop looks exactly like one never started. -->
                    <div v-if="finishPending" class="body-2 text-center orange--text text--darken-4 mt-1">
                        ลูกค้าเซ็นแล้ว — เหลือถ่ายรูปใบส่งของและกดปิดงาน
                    </div>
                </template>
                <v-alert v-else-if="failed" type="error" text>
                    ส่งไม่สำเร็จ
                    <div v-if="rejectText" class="body-2">{{ rejectText }}</div>
                </v-alert>
                <v-alert v-else type="success" text>
                    งานนี้เสร็จแล้ว
                    <div v-if="followUpText" class="body-2 orange--text text--darken-4">
                        ต้องติดตามงาน: {{ followUpText }}
                    </div>
                </v-alert>

                <!-- ส่งไม่สำเร็จ, from arrival until the job is finished: ลูกค้าไม่อยู่
                     and สถานที่จัดส่งผิด are known at the door, a defect or a
                     cabinet that will not assemble only once work has started. -->
                <v-btn v-if="canReject" block large outlined color="red" class="mt-3"
                    :disabled="busy" @click="rejectOpen = true">
                    <v-icon left>mdi-close-octagon-outline</v-icon>แจ้งส่งไม่สำเร็จ
                </v-btn>

                <!-- The signed ใบส่งของ is required on the finish sheet, but a
                     driver may have had to give a reason instead (no signal, no
                     paper yet). This lets them add the photo once they can,
                     without touching anything else the sheet saved. -->
                <v-btn v-if="stop.status === 'done' && !receiptSaved" block large outlined
                    color="orange accent-4" class="mt-3" :disabled="busy" @click="openAdd('receipt')">
                    <v-icon left>mdi-file-document-outline</v-icon>ถ่ายรูปใบส่งของ
                </v-btn>

                <!-- A photo forgotten at any step, added once the job is closed.
                     Each stage keeps its limit; an added photo goes after the
                     ones already there and never replaces one. -->
                <v-btn v-if="addStages.some(a => a.room > 0)" block large outlined
                    color="grey darken-2" class="mt-3" :disabled="busy" @click="openAdd('')">
                    <v-icon left>mdi-camera-plus-outline</v-icon>เพิ่มรูปภาพ
                </v-btn>

                <v-snackbar v-model="showError" color="red" :timeout="5000">{{ error }}</v-snackbar>

                <!-- ถึงบ้านลูกค้า and เริ่มติดตั้ง take their photos here, before the
                     stamp: the front of the house proves the address, the room
                     before shows what the team found. `stageStep` is fixed when
                     the sheet opens, so a reload behind it cannot turn the house
                     sheet into the room sheet mid-photo. -->
                <v-dialog v-model="stageOpen" persistent max-width="480" scrollable>
                    <v-card v-if="stageInfo">
                        <v-card-title class="subtitle-1">{{ stepLabel(stageStep) || stageTitle }}</v-card-title>
                        <v-card-text>
                            <div class="subtitle-2 black--text mb-1">
                                {{ stageTitle }}
                                <span class="grey--text">(ไม่เกิน {{ stageInfo.max }} รูป)</span>
                            </div>
                            <photoCapture ref="stagePhotos" v-model="stagePhotos" :max="stageInfo.max"
                                :disabled="busy"></photoCapture>
                            <v-select v-if="!stagePhotos.length" v-model="stageSkippedReason"
                                :items="stageReasons" dense clearable class="mt-5"
                                label="ถ้าถ่ายรูปไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="stageOpen = false" :disabled="busy">ยกเลิก</v-btn>
                            <span v-if="uploadNote" class="body-2 grey--text text--darken-1 ml-2">{{ uploadNote }}</span>
                            <v-spacer></v-spacer>
                            <v-btn color="green" dark :loading="busy" :disabled="busy" @click="stampWithPhotos">บันทึก</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- เพิ่มรูปภาพ: photos added to a closed stop. Which stage first,
                     then the photos, up to the room that stage has left. -->
                <v-dialog v-model="addOpen" persistent max-width="480" scrollable>
                    <v-card>
                        <v-card-title class="subtitle-1">เพิ่มรูปภาพ</v-card-title>
                        <v-card-text>
                            <div class="body-2 grey--text text--darken-1 mb-2">เลือกขั้นตอนที่ต้องการเพิ่มรูป</div>
                            <!-- The chosen chip is filled, the rest outlined: an
                                 outlined chip has no background, so white text on
                                 the chosen one read as an empty chip. -->
                            <v-chip-group v-model="addStep" column mandatory>
                                <v-chip v-for="a in addStages" :key="a.step" :value="a.step"
                                    :disabled="busy || a.room === 0" :outlined="addStep !== a.step"
                                    :color="addStep === a.step ? 'orange accent-4' : undefined"
                                    :class="{ 'white--text font-weight-bold': addStep === a.step }">
                                    {{ a.short }} ({{ a.have }}/{{ a.have + a.room }})
                                </v-chip>
                            </v-chip-group>
                            <template v-if="addInfo">
                                <div class="subtitle-2 black--text mt-3 mb-1">
                                    {{ addInfo.title }}
                                    <span class="grey--text">(เพิ่มได้อีก {{ addInfo.room }} รูป)</span>
                                </div>
                                <photoCapture ref="addPhotos" v-model="addPhotos" :max="addInfo.room"
                                    :disabled="busy"></photoCapture>
                            </template>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="addOpen = false" :disabled="busy">ยกเลิก</v-btn>
                            <span v-if="uploadNote" class="body-2 grey--text text--darken-1 ml-2">{{ uploadNote }}</span>
                            <v-spacer></v-spacer>
                            <v-btn color="green" dark :loading="busy" :disabled="busy || !addPhotos.length" @click="saveAdded">บันทึก</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- ใบยินยอมรับบริการติดตั้ง, the customer's, before เริ่มติดตั้ง:
                     read, tick, sign. Saved on its own, ahead of the "before"
                     photos and the stamp, so a reopened เริ่มติดตั้ง goes straight
                     to the photos. The only way past it unsigned is ระบบขัดข้อง;
                     a customer who will not consent is a ส่งไม่สำเร็จ. The wording
                     and its rules live in consentForm.js. -->
                <v-dialog v-model="consentOpen" persistent max-width="480" scrollable>
                    <v-card>
                        <v-card-title class="subtitle-1 font-weight-bold">
                            {{ consentTitle }}
                            <v-spacer></v-spacer>
                            <v-chip small color="green" dark>สำหรับลูกค้า</v-chip>
                        </v-card-title>
                        <v-card-text class="black--text">
                            <div class="grey lighten-3 rounded pa-2 mb-3 body-2">
                                <div><b>วันที่:</b> {{ consentHeader.date }}</div>
                                <div><b>เลขที่ SO:</b> {{ consentHeader.docNo || '-' }}</div>
                                <div><b>ชื่อลูกค้า:</b> {{ consentHeader.custName || '-' }}</div>
                            </div>

                            <div class="body-2 mb-1">ลูกค้าตกลงรับบริการตามเงื่อนไขดังนี้</div>
                            <div v-for="t in consentTerms" :key="t.head" class="d-flex body-2 mb-1">
                                <v-icon small color="orange accent-4" class="mr-2 align-self-start mt-1">{{ t.icon }}</v-icon>
                                <div><b>{{ t.head }}:</b> {{ t.text }}</div>
                            </div>
                            <a class="body-2" @click="consentDoc = 'terms'">
                                <v-icon small color="primary">mdi-link-variant</v-icon>
                                คลิกอ่านเงื่อนไขและข้อตกลงฉบับเต็ม
                            </a>

                            <v-divider class="my-3"></v-divider>
                            <v-checkbox v-model="consentAccepted" hide-details class="mt-0" color="green">
                                <template v-slot:label>
                                    <span class="body-2 black--text">{{ consentText }}</span>
                                </template>
                            </v-checkbox>
                            <!-- Outside the checkbox's label, so opening the notice
                                 does not also tick the box. -->
                            <a class="body-2 d-inline-block ml-8 mb-3" @click="consentDoc = 'privacy'">
                                <v-icon small color="primary">mdi-shield-account-outline</v-icon>
                                นโยบายความเป็นส่วนตัว (Privacy Notice)
                            </a>

                            <div class="subtitle-2 mb-1">ลายเซ็นลูกค้า</div>
                            <signaturePad ref="consentPad"></signaturePad>
                            <v-text-field v-model="consentName" label="ชื่อผู้ลงนาม" dense
                                color="orange accent-3"></v-text-field>
                            <v-select v-model="consentSkippedReason" :items="consentReasons" dense clearable
                                label="(พนักงาน) ถ้าเซ็นไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>
                            <div class="caption grey--text text--darken-1">
                                ลูกค้าไม่ยินยอม ให้กดยกเลิก แล้วแจ้งส่งไม่สำเร็จ
                            </div>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="consentOpen = false" :disabled="busy">ยกเลิก</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="green darken-1" dark :loading="busy"
                                :disabled="busy || (!consentAccepted && !consentSkippedReason)"
                                @click="saveConsentPage">
                                {{ consentSkippedReason ? 'บันทึก (' + consentSkippedReason + ')' : 'ยืนยันและเซ็นชื่อ' }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- The full terms and the privacy notice, over the consent
                     sheet. In the app rather than a web link, so they open with
                     no signal at the customer's door. -->
                <v-dialog :value="!!consentDoc" @input="v => { if (!v) consentDoc = '' }" max-width="480" scrollable>
                    <v-card>
                        <v-card-title class="subtitle-1 font-weight-bold">
                            {{ consentDoc === 'privacy' ? 'นโยบายความเป็นส่วนตัว' : 'เงื่อนไขและข้อตกลงฉบับเต็ม' }}
                            <v-chip v-if="consentDraft" small color="red" dark class="ml-2">ฉบับร่าง</v-chip>
                        </v-card-title>
                        <v-card-text class="black--text">
                            <p v-for="(line, i) in consentDocLines" :key="i" class="body-2">{{ line }}</p>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn text @click="consentDoc = ''">ปิด</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- ปิดงาน, in three pages because two people fill it in.
                     1 (driver)   photographs the installed work
                     2 (customer) rates, signs and presses บันทึก themselves --
                                  customers asked to, and a phone signature treated
                                  as the end used to leave the stars blank, so the
                                  stars come first
                     3 (driver)   photographs the paper ใบส่งของ the customer signs
                                  after the phone, flags any follow-up, and closes
                     Page 2 saves on its own, so a sheet reopened after it opens
                     on page 3 instead of handing the phone back to the customer.
                     v-show, not v-if: signaturePad must stay mounted, or the
                     signature would be lost by stepping back to page 1. -->
                <v-dialog v-model="finishOpen" persistent max-width="480" scrollable>
                    <v-card>
                        <v-card-title class="subtitle-1">
                            ปิดงาน
                            <v-spacer></v-spacer>
                            <v-chip small :color="finishPage === 2 ? 'green' : 'blue-grey'" dark>
                                {{ finishPage === 2 ? 'สำหรับลูกค้า' : 'สำหรับพนักงานขับรถ' }} · {{ finishPage }}/3
                            </v-chip>
                        </v-card-title>
                        <v-card-text>
                            <div v-show="finishPage === 1">
                                <div class="subtitle-2 black--text mb-1">
                                    {{ finishTitle }}
                                    <span class="grey--text">(ไม่เกิน {{ finishStage.max }} รูป)</span>
                                </div>
                                <photoCapture ref="photos" v-model="photos" :max="finishStage.max"
                                    :disabled="busy"></photoCapture>
                                <v-select v-if="!photos.length" v-model="photoSkippedReason"
                                    :items="photoReasons" dense clearable class="mt-5"
                                    label="ถ้าถ่ายรูปไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>
                            </div>

                            <div v-show="finishPage === 2">
                                <div class="subtitle-2 black--text mb-1">ความพึงพอใจต่อการจัดส่งและติดตั้ง</div>
                                <v-rating v-model="rating" color="orange accent-4" large
                                    background-color="grey lighten-1" hover></v-rating>
                                <v-checkbox v-model="ratingDeclined" label="ไม่ประสงค์ให้คะแนน"
                                    dense hide-details class="mt-0" color="orange accent-4"></v-checkbox>
                                <v-textarea v-model="comment" label="ความเห็นเพิ่มเติม" rows="2"
                                    dense class="mt-3" color="orange accent-3"></v-textarea>

                                <div class="subtitle-2 black--text mb-1">ลงชื่อรับสินค้า</div>
                                <signaturePad ref="pad"></signaturePad>
                                <v-text-field v-model="signedName" label="ชื่อผู้เซ็น" dense
                                    color="orange accent-3"></v-text-field>
                                <v-select v-model="refusedReason" :items="reasons" dense clearable
                                    label="ถ้าเซ็นไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>
                            </div>

                            <div v-show="finishPage === 3">
                                <div class="subtitle-2 black--text mb-1">
                                    {{ receiptTitle }}
                                    <span class="grey--text">(ไม่เกิน {{ receiptStage.max }} รูป)</span>
                                </div>
                                <div class="body-2 grey--text text--darken-1 mb-1">
                                    {{ absent ? 'ถ่ายรูปใบส่งของ/ใบเสร็จที่วางไว้กับสินค้า' : 'ให้ลูกค้าเซ็นใบส่งของ/ใบเสร็จ แล้วถ่ายรูปให้เห็นลายเซ็นชัดเจน' }}
                                </div>
                                <photoCapture ref="receiptPhotos" v-model="receiptPhotos" :max="receiptStage.max"
                                    :disabled="busy"></photoCapture>
                                <!-- mt-5 on every reason select under a photo button: a
                                     dense field floats its label ~16px up when opened,
                                     and mt-2 left it drawn over the button. -->
                                <v-select v-if="!receiptPhotos.length" v-model="receiptSkippedReason"
                                    :items="receiptReasons" dense clearable class="mt-5"
                                    label="ถ้าถ่ายรูปไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>

                                <v-divider class="my-3"></v-divider>
                                <!-- The customer kept the products but needs more
                                     support: the job is still delivered, and the
                                     monitor board flags it. -->
                                <v-checkbox v-model="followUp" label="ลูกค้ารับสินค้า แต่ต้องติดตามงานเพิ่ม"
                                    dense hide-details class="mt-0" color="orange accent-4"></v-checkbox>
                                <div v-if="followUp" class="mt-2">
                                    <div class="body-2 mb-1">เหตุผล (เลือกได้หลายข้อ)</div>
                                    <!-- A chip is outlined only while unselected: Vuetify
                                         keeps an outlined chip's background transparent,
                                         so the active class's white text on a selected
                                         outlined chip was white on white. -->
                                    <v-chip-group v-model="followUpReasons" multiple column
                                        active-class="orange accent-4 white--text">
                                        <v-chip v-for="r in followUpReasonList" :key="r" :value="r" filter
                                            :outlined="followUpReasons.indexOf(r) < 0">
                                            {{ r }}
                                        </v-chip>
                                    </v-chip-group>
                                    <v-textarea v-model="followUpNote" label="รายละเอียด" rows="2" dense
                                        color="orange accent-3"></v-textarea>
                                </div>
                            </div>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn v-if="finishPage === 1" text @click="finishOpen = false" :disabled="busy">ยกเลิก</v-btn>
                            <v-btn v-else-if="finishPage === 2" text @click="finishPage = 1" :disabled="busy">ย้อนกลับ</v-btn>
                            <!-- Page 3 can be left and come back to: the customer's
                                 part is already saved. -->
                            <v-btn v-else text @click="finishOpen = false" :disabled="busy">ภายหลัง</v-btn>
                            <span v-if="uploadNote" class="body-2 grey--text text--darken-1 ml-2">{{ uploadNote }}</span>
                            <v-spacer></v-spacer>
                            <v-btn v-if="finishPage === 1" color="orange accent-4" dark :disabled="busy"
                                @click="toCustomerPage">ถัดไป: ให้ลูกค้าเซ็น</v-btn>
                            <v-btn v-else-if="finishPage === 2" color="green" dark :loading="busy" :disabled="busy"
                                @click="saveCustomerPage">บันทึก</v-btn>
                            <v-btn v-else color="green" dark :loading="busy" :disabled="busy"
                                @click="closeJob">ปิดงาน</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- ส่งไม่สำเร็จ: the customer did not accept the products. Saving
                     records the reasons and evidence, then the `rejected` stamp,
                     which closes the stop for the driver. Delivery rebooks it. -->
                <v-dialog v-model="rejectOpen" persistent max-width="480" scrollable>
                    <v-card>
                        <v-card-title class="subtitle-1 red--text">แจ้งส่งไม่สำเร็จ</v-card-title>
                        <v-card-text>
                            <div class="body-2 mb-1">ลูกค้าไม่รับสินค้า เพราะ (เลือกได้หลายข้อ)</div>
                            <v-chip-group v-model="rejectReasons" multiple column active-class="red white--text">
                                <v-chip v-for="r in rejectReasonList" :key="r" :value="r" filter
                                    :outlined="rejectReasons.indexOf(r) < 0">
                                    {{ r }}
                                </v-chip>
                            </v-chip-group>
                            <v-textarea v-model="rejectNote" label="รายละเอียด" rows="2" dense
                                color="orange accent-3"></v-textarea>

                            <div class="subtitle-2 black--text mb-1">
                                {{ rejectStage.title }}
                                <span class="grey--text">(ไม่เกิน {{ rejectStage.max }} รูป)</span>
                            </div>
                            <photoCapture ref="rejectPhotos" v-model="rejectPhotos" :max="rejectStage.max"
                                :disabled="busy"></photoCapture>
                            <v-select v-if="!rejectPhotos.length" v-model="rejectSkippedReason"
                                :items="photoReasons" dense clearable class="mt-5"
                                label="ถ้าถ่ายรูปไม่ได้ เลือกเหตุผล" color="orange accent-3"></v-select>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="rejectOpen = false" :disabled="busy">ยกเลิก</v-btn>
                            <span v-if="uploadNote" class="body-2 grey--text text--darken-1 ml-2">{{ uploadNote }}</span>
                            <v-spacer></v-spacer>
                            <v-btn color="red" dark :loading="busy" :disabled="busy" @click="saveReject">
                                บันทึกส่งไม่สำเร็จ
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-container>

            <v-container v-else>
                <v-progress-linear indeterminate color="orange accent-3"></v-progress-linear>
            </v-container>
        </v-main>
    </v-app>
</template>
<script>
import driverBar from '@/components/driver/driverBar'
import signaturePad from '@/components/driver/signaturePad'
import photoCapture from '@/components/driver/photoCapture'
import driverService from '@/components/driverService'
import { pushDeliveredToSap } from '@/components/sapDelivered'
import D from '@/components/driverDay'
import P from '@/components/proofPhoto'
import O from '@/components/deliveryOutcome'
import C from '@/components/consentForm'
import moment from 'moment-timezone'

// Same zone driverService.js's todayISO/nowClock use. A stamp's `atDevice` is
// always written as new Date().toISOString() -- UTC -- so substringing it
// directly (as an earlier draft of this view did) rendered every stamp seven
// hours early. This is the only place a driver sees what was actually
// recorded, so it must show a right value, not just a present one.
const TZ = 'Asia/Bangkok'

// Anything driverService throws without a recognised e.code (a raw
// Firebase/network message, which is English) falls back to this generic
// Thai retry message rather than being shown as-is. A recognised code (e.g.
// driverService.BAD_STEP_ORDER) shows that error's own Thai message instead
// -- matched by e.code, never by the message text, per the comment near
// driverService.js's NOT_SIGNED_IN.
const GENERIC_STAMP_ERROR = 'บันทึกเวลาไม่สำเร็จ กรุณาลองใหม่'

export default {
    components: { driverBar, signaturePad, photoCapture },
    props: { id: { type: String, required: true } },
    data () {
        return {
            stop: null, plate: '', busy: false, error: '', showError: false,
            finishOpen: false,
            // Which page of ปิดงาน is showing: 1 driver, 2 customer, 3 driver.
            finishPage: 1,
            signedName: '',
            rating: 0,
            ratingDeclined: false,
            comment: '',
            refusedReason: '',
            // Installation photos, as { blob, url } -- shrunk on the phone as
            // they are taken, uploaded only when the sheet is saved.
            photos: [],
            photoSkippedReason: '',
            photoReasons: P.SKIP_REASONS,
            finishStage: P.STAGES.finished,
            // The signed paper ใบส่งของ, photographed last on the same sheet.
            receiptPhotos: [],
            receiptSkippedReason: '',
            receiptStage: P.STAGES.receipt,
            receiptReasons: P.reasonsFor('receipt'),
            // ต้องติดตามงาน, on page 3.
            followUp: false,
            followUpReasons: [],
            followUpNote: '',
            followUpReasonList: O.FOLLOW_UP_REASONS,
            // ส่งไม่สำเร็จ.
            rejectOpen: false,
            rejectReasons: [],
            rejectNote: '',
            rejectPhotos: [],
            rejectSkippedReason: '',
            rejectStage: P.STAGES.rejected,
            rejectReasonList: O.REJECT_REASONS,
            uploadNote: '',
            // ใบยินยอมรับบริการติดตั้ง, before เริ่มติดตั้ง.
            consentOpen: false,
            consentAccepted: false,
            consentName: '',
            consentSkippedReason: '',
            consentReasons: C.SKIP_REASONS,
            consentTitle: C.TITLE,
            consentTerms: C.TERMS,
            consentText: C.CONSENT_TEXT,
            consentDraft: C.DRAFT,
            // Which long text is open over the sheet: 'terms', 'privacy' or ''.
            consentDoc: '',
            // The ถึงบ้านลูกค้า / เริ่มติดตั้ง photo sheet.
            stageOpen: false,
            stageStep: '',
            stagePhotos: [],
            stageSkippedReason: '',
            // เพิ่มรูปภาพ, on a closed stop.
            addOpen: false,
            addStep: '',
            addPhotos: [],
            // 'ระบบขัดข้อง' is deliberately separate from 'ปฏิเสธเซ็น'. "The app
            // broke" and "the customer refused" must not look the same to whoever
            // reads this later.
            reasons: ['ลูกค้าไม่อยู่', 'ปฏิเสธเซ็น', 'มือถือไม่ได้', 'ระบบขัดข้อง']
        }
    },
    computed: {
        steps () { return D.STEPS.map(k => ({ key: k, label: D.stampLabel(this.stop, k) })) },
        next () { return this.stop ? D.nextStep(this.stop) : null },
        nextLabel () {
            if (this.finishPending) return D.stampLabel(this.stop, 'finished') + ' (3/3 ใบส่งของ)'
            return this.next ? D.stampLabel(this.stop, this.next) : ''
        },
        // เสร็จงาน is next and the customer's page is already saved, so the
        // sheet will open on page 3.
        finishPending () { return D.awaitingClose(this.stop) },
        stageInfo () { return P.stageFor(this.stageStep) },
        // Nobody home: the consent was passed as ลูกค้าไม่อยู่, so the products
        // are being left, not installed, and the photo titles say so.
        absent () { return C.customerAbsent(this.stop) },
        stageTitle () { return P.titleFor(this.stageStep, this.absent) },
        finishTitle () { return P.titleFor('finished', this.absent) },
        receiptTitle () { return P.titleFor('receipt', this.absent) },
        consentHeader () { return C.headerFor(this.stop) },
        consentDocLines () {
            if (this.consentDoc === 'privacy') return C.PRIVACY_NOTICE
            return this.consentDoc === 'terms' ? C.FULL_TERMS : []
        },
        // The stages this closed stop may still add photos to (none while
        // it is open: the stage sheets take them then).
        addStages () { return this.stop ? P.addableStages(this.stop, this.absent) : [] },
        addInfo () { return this.addStages.find(a => a.step === this.addStep) || null },
        stageReasons () { return P.reasonsFor(this.stageStep) },
        productLines () {
            return this.stop ? this.stop.lines.filter(l => (l.products && l.products.length) || (l.returns && l.returns.length)) : []
        },
        productCount () {
            return this.productLines.reduce((n, l) => n + l.products.reduce((m, p) => m + p.qty, 0), 0)
        },
        failed () { return !!this.stop && this.stop.status === 'failed' },
        canReject () { return !!this.stop && !this.busy && D.canStamp(this.stop, D.REJECTED) },
        rejectText () {
            const proof = (this.stop && this.stop.proof) || {}
            return O.reasonText(proof.rejectReasons, proof.rejectNote)
        },
        followUpText () {
            const proof = (this.stop && this.stop.proof) || {}
            return proof.followUp ? O.reasonText(proof.followUpReasons, proof.followUpNote) : ''
        },
        // The customer's page has been saved for this stop: set by
        // saveCustomerProof, so it survives the driver closing the app.
        customerSaved () {
            const proof = (this.stop && this.stop.proof) || {}
            return !!proof.capturedAt
        },
        receiptSaved () {
            const proof = (this.stop && this.stop.proof) || {}
            return ((proof[P.STAGES.receipt.urlsField]) || []).length > 0
        },
        navUrl () { return this.stop ? D.mapsUrl(this.stop) : '' },
        // Keyed off the precision geo.js reports, not off the presence of a
        // `geo` string: an unparseable pin resolves to the address like any
        // other row, and must not be described as confirmed.
        navPrecision () {
            return (this.stop && this.stop.location && this.stop.location.precision) || null
        },
        navBasis () {
            if (!this.navUrl) return 'ไม่มีข้อมูลสำหรับนำทาง'
            if (this.navPrecision === 'exact') return 'พิกัดที่ยืนยันกับลูกค้าแล้ว'
            return 'นำทางตามที่อยู่ — อาจคลาดเคลื่อน โทรหาลูกค้าเมื่อใกล้ถึง'
        },
        navBasisClass () {
            if (!this.navUrl) return 'grey--text'
            return this.navPrecision === 'exact'
                ? 'green--text text--darken-2'
                : 'orange--text text--darken-3'
        }
    },
    watch: {
        // The same reset as the finish sheet below, for the same reason: the
        // previous stop's house must never be submitted as this one's.
        stageOpen (open) {
            if (!open) return
            if (this.$refs.stagePhotos) this.$refs.stagePhotos.release()
            this.stagePhotos = []
            this.stageSkippedReason = ''
            this.uploadNote = ''
        },
        // The same reset as the finish sheet's, for the same reason: the previous
        // customer's tick and signature must never be submitted as this one's.
        consentOpen (open) {
            if (!open) return
            this.consentAccepted = false
            this.consentName = (this.stop && this.stop.custName) || ''
            this.consentSkippedReason = ''
            this.consentDoc = ''
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const pad = this.$refs.consentPad
                    if (pad) pad.clear()
                })
            })
        },
        // The same reset as the stage sheet's: one stage's photos must never
        // be saved under another, nor a previous stop's under this one.
        addOpen (open) {
            if (!open) return
            if (this.$refs.addPhotos) this.$refs.addPhotos.release()
            this.addPhotos = []
            this.uploadNote = ''
        },
        addStep () {
            if (this.$refs.addPhotos) this.$refs.addPhotos.release()
            this.addPhotos = []
        },
        // Stars and "declined" are two answers to one question; picking one
        // clears the other, so a sheet can never save both.
        rating (value) { if (value) this.ratingDeclined = false },
        ratingDeclined (value) { if (value) this.rating = 0 },
        // The same reset again: the previous stop's evidence must never be
        // submitted as this one's.
        rejectOpen (open) {
            if (!open) return
            if (this.$refs.rejectPhotos) this.$refs.rejectPhotos.release()
            this.rejectReasons = []
            this.rejectNote = ''
            this.rejectPhotos = []
            this.rejectSkippedReason = ''
            this.uploadNote = ''
        },
        // v-dialog only mounts signaturePad on its FIRST open and keeps it
        // mounted (not re-created) across later opens, so nothing would
        // otherwise reset a previous signature, the form fields beside it, or
        // re-measure the canvas's width for a re-opened sheet. Left alone,
        // the PREVIOUS customer's signature, name, rating and comment would
        // still be sitting there for the NEXT one to (silently) submit --
        // an evidence-integrity and privacy problem, not just a UI one.
        // $nextTick alone can still land before the dialog's own transition
        // has settled the card's layout width, so an extra animation frame is
        // given before measuring.
        finishOpen (open) {
            if (!open) return
            this.finishPage = this.customerSaved ? 3 : 1
            this.followUp = false
            this.followUpReasons = []
            this.followUpNote = ''
            this.signedName = C.signerDefault(this.stop)
            this.rating = 0
            this.ratingDeclined = false
            this.comment = ''
            // Nobody home at เริ่มติดตั้ง is nobody home now: start page 2 on the
            // same answer, so the driver is not asked to say it twice.
            this.refusedReason = C.customerAbsent(this.stop) ? C.ABSENT : ''
            // Photos too, for the same reason as the signature: the previous
            // customer's living room must not be submitted as this one's.
            if (this.$refs.photos) this.$refs.photos.release()
            this.photos = []
            this.photoSkippedReason = ''
            if (this.$refs.receiptPhotos) this.$refs.receiptPhotos.release()
            this.receiptPhotos = []
            // Nobody home to sign the paper either: start on the same answer.
            // A photo of the receipt left with the products still beats it.
            this.receiptSkippedReason = this.absent ? C.ABSENT : ''
            this.uploadNote = ''
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const pad = this.$refs.pad
                    if (pad) pad.clear()
                })
            })
        }
    },
    methods: {
        lineRef (line) { return D.lineRef(line) },
        stamped (key) {
            // Aligned with D.nextStep / D.statusFor, which both require
            // `.at != null` -- not mere truthiness -- so a step can never render
            // as done here while the button below still offers it as next.
            const s = this.stop && this.stop.steps && this.stop.steps[key]
            return !!(s && s.at != null)
        },
        stampClock (key) {
            const s = this.stop && this.stop.steps && this.stop.steps[key]
            if (!s || !s.atDevice) return ''
            const m = moment.tz(s.atDevice, TZ)
            return m.isValid() ? m.format('HH:mm') : ''
        },
        // Never blocks the stamp. A denied permission or a phone that cannot get
        // a fix in three seconds records the stamp with no coordinates, because a
        // driver standing in a customer's living room must not be stuck waiting
        // for GPS.
        readCoords () {
            return new Promise(resolve => {
                if (!navigator.geolocation) return resolve(null)
                let settled = false
                let timer = null
                const done = value => {
                    if (settled) return
                    settled = true
                    if (timer) clearTimeout(timer)
                    resolve(value)
                }
                timer = setTimeout(() => done(null), 3000)
                navigator.geolocation.getCurrentPosition(
                    pos => done({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
                    () => done(null),
                    { enableHighAccuracy: true, timeout: 3000, maximumAge: 30000 }
                )
            })
        },
        // Shared by stamp(), closeJob() and saveReject(): the local, optimistic reflection of a
        // stamp the server has just accepted, applied BEFORE the reload that
        // follows. If that reload's own network call fails, `this.stop` must
        // never be left holding pre-stamp state -- that stale state is exactly
        // what would let a second tap pass D.canStamp's ordering check and
        // overwrite the first stamp's server timestamp (and, for `finished`,
        // its coords, `timeStampEnd`, and append a duplicate timeline entry).
        // Vue 2 does not detect a new property added to an existing object,
        // hence $set.
        applyLocalStamp (step) {
            this.$set(this.stop.steps, step, {
                at: new Date().toISOString(),
                atDevice: new Date().toISOString()
            })
            this.stop.status = D.statusFor(this.stop.steps)
        },
        stepLabel (step) { return D.stampLabel(this.stop, step) },
        async stamp () {
            // Vuetify's `loading` prop only draws a spinner -- it does not disable
            // the button, so a fast double-tap can fire this twice before the
            // `:disabled` binding re-renders. Guard in-method as well: without it,
            // two taps evaluate D.nextStep against the same in-memory stop, both
            // pass, and the second write overwrites the first stamp's server
            // timestamp -- exactly what "a stamp never overwrites a stamp" exists
            // to prevent.
            if (this.busy) return
            if (!this.next) return
            // เสร็จงาน collects photos AND the signature, on its own sheet.
            if (this.next === 'finished') { this.finishOpen = true; return }
            // เริ่มติดตั้ง: the customer's consent comes before anything else.
            if (this.next === 'installStarted' && C.consentRequired(this.stop) && !C.consentDone(this.stop)) {
                this.consentOpen = true
                return
            }
            // ถึงบ้านลูกค้า and เริ่มติดตั้ง collect photos first.
            if (P.stampStageFor(this.next)) {
                this.stageStep = this.next
                this.stageOpen = true
                return
            }
            // ออกเดินทาง: still one tap.
            const step = this.next
            this.busy = true
            try {
                await this.recordStamp(step)
            } catch (e) {
                await this.stampFailed(e, GENERIC_STAMP_ERROR)
            }
            this.busy = false
        },
        // The customer's ยืนยันและเซ็นชื่อ: the consent is saved on its own, then
        // the เริ่มติดตั้ง photo sheet opens. No stamp here -- the stamp still
        // follows the photos, so it never exists without either.
        async saveConsentPage () {
            if (this.busy) return
            const pad = this.$refs.consentPad
            const hasSignature = !!pad && !pad.isEmpty()
            const problem = C.consentProblem({
                hasSignature: hasSignature,
                accepted: this.consentAccepted,
                skippedReason: this.consentSkippedReason
            })
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            if (!D.canStamp(this.stop, 'installStarted')) {
                this.consentOpen = false
                this.error = 'จุดนี้ถูกบันทึกขั้นตอนนี้แล้วจากอุปกรณ์อื่น'
                this.showError = true
                await this.load()
                return
            }
            this.busy = true
            try {
                // A signature beats a reason: a customer who did sign is
                // recorded as signed even if the driver also picked one.
                const signatureUrl = hasSignature
                    ? await driverService.uploadConsentSignature(this.stop, pad.toDataUrl())
                    : ''
                const input = {
                    signatureUrl: signatureUrl,
                    signedName: this.consentName,
                    accepted: this.consentAccepted,
                    skippedReason: this.consentSkippedReason,
                    atDevice: new Date().toISOString()
                }
                await driverService.saveConsent(this.stop, input)
                // Locally too, so tapping เริ่มติดตั้ง again before a reload goes
                // to the photos rather than back to the customer.
                this.$set(this.stop, 'proof', Object.assign({}, this.stop.proof, C.consentFields(input)))
                this.consentOpen = false
                this.stageStep = 'installStarted'
                this.stageOpen = true
            } catch (e) {
                if (e && e.code === driverService.NOT_SIGNED_IN) {
                    driverService.disarmPing()
                    this.consentOpen = false
                    this.$router.push({ name: 'driverPin' }).catch(() => {})
                } else {
                    this.error = 'บันทึกใบยินยอมไม่สำเร็จ กรุณาลองใหม่ หรือเลือกเหตุผล "ระบบขัดข้อง"'
                    this.showError = true
                }
            }
            this.busy = false
        },
        // เพิ่มรูปภาพ, opened on `step` (ถ่ายรูปใบส่งของ) or on the first stage
        // with room left.
        openAdd (step) {
            if (this.busy) return
            const open = this.addStages.filter(a => a.room > 0)
            if (!open.length) return
            const pick = open.find(a => a.step === step) || open[0]
            this.addStep = pick.step
            this.addOpen = true
        },
        // Uploaded after the photos the stage already holds -- the next free
        // file names -- and appended in a transaction, so nothing is replaced.
        async saveAdded () {
            if (this.busy) return
            const info = this.addInfo
            if (!info || !this.addPhotos.length) return
            if (this.addPhotos.length > info.room) {
                this.error = 'เพิ่มได้อีกไม่เกิน ' + info.room + ' รูป'
                this.showError = true
                return
            }
            this.busy = true
            try {
                const urls = await this.uploadWithNote(info.step, this.addPhotos, info.have)
                await driverService.addLatePhotos(this.stop, info.step, urls)
                this.addOpen = false
                await this.load()
            } catch (e) {
                await this.stampFailed(e, 'บันทึกรูปไม่สำเร็จ กรุณาลองใหม่')
            }
            this.busy = false
        },
        async stampWithPhotos () {
            // Same double-tap guard as stamp(); a second tap would upload the
            // photos twice and try the stamp twice.
            if (this.busy) return
            const step = this.stageStep
            const problem = P.photoProblem(step, this.stagePhotos.length, this.stageSkippedReason, this.absent)
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            // Refuse BEFORE uploading when this device already knows the step
            // is taken: the file names are fixed, so uploading anyway would
            // replace the photos another phone recorded with ours and only then
            // have the stamp refused. (The transaction in stampStep still
            // settles a true race between two phones.)
            if (!D.canStamp(this.stop, step)) {
                this.stageOpen = false
                this.error = 'จุดนี้ถูกบันทึกขั้นตอนนี้แล้วจากอุปกรณ์อื่น'
                this.showError = true
                await this.load()
                return
            }
            this.busy = true
            try {
                // Photos before the stamp, so a stamp never exists without the
                // photos the driver was asked for. A failed upload keeps the
                // sheet open with the photos in it; fixed names make the retry
                // overwrite, not add.
                const urls = await this.uploadWithNote(step, this.stagePhotos)
                await driverService.savePhotos(this.stop, step, urls, this.stageSkippedReason)
                await this.recordStamp(step)
                this.stageOpen = false
            } catch (e) {
                // Another device stamped this step already. As in closeJob():
                // close the sheet and let the reload show it, rather than ask
                // for a retry that is guaranteed to fail.
                if (e && e.code === driverService.BAD_STEP_ORDER) this.stageOpen = false
                await this.stampFailed(e, 'บันทึกรูปไม่สำเร็จ กรุณาลองใหม่ หรือเลือกเหตุผล "ระบบขัดข้อง"')
            }
            this.busy = false
        },
        // Uploads one stage's photos, saying which one is on its way. Returns
        // [] for no photos, so a skipped stage writes its reason and no URLs.
        // `start` is where added photos begin (see saveAdded).
        async uploadWithNote (step, photos, start) {
            if (!photos.length) return []
            this.uploadNote = 'กำลังส่งรูป 0/' + photos.length
            try {
                return await driverService.uploadProofPhotos(
                    this.stop, step, photos.map(p => p.blob),
                    (done, n) => { this.uploadNote = 'กำลังส่งรูป ' + done + '/' + n }, start)
            } finally {
                this.uploadNote = ''
            }
        },
        // The stamp itself, shared by the one-tap ออกเดินทาง and the two photo
        // sheets. Throws; the caller decides what the driver is told.
        async recordStamp (step) {
            const coords = await this.readCoords()
            await driverService.stampStep(this.stop, step, coords)
            // The server has accepted the write. Reflect it in the local copy
            // BEFORE attempting the reload below: if that reload fails (a
            // network blip right after a write is common, not exotic),
            // `this.stop` must never be left holding pre-stamp state -- that
            // stale state is exactly what let a second tap pass D.canStamp's
            // ordering check and overwrite the first stamp's server
            // timestamp.
            this.applyLocalStamp(step)
            // The truck is moving: start reporting where it is. The coords
            // just captured for the stamp are ping #1, so the board is not
            // blank for the first ten minutes of the leg. Arrival ends it --
            // "where is the truck" has an obvious answer once it is parked
            // at the customer.
            if (step === 'departed') driverService.armPing(this.stop, coords)
            if (step === 'arrived') driverService.disarmPing()
            await this.load()
        },
        async stampFailed (e, genericMessage) {
            if (e && e.code === driverService.NOT_SIGNED_IN) {
                // Not a stamp failure -- there is nobody to stamp for. Sending
                // the driver back to the keypad is the only useful response;
                // reporting it as "บันทึกไม่สำเร็จ" would just send them
                // tapping the button forever.
                //
                // Release the ping timer on the way out. Every other exit
                // from a leg disarms it; session expiry was the one that
                // left the interval and its two listeners running.
                driverService.disarmPing()
                this.stageOpen = false
                this.$router.push({ name: 'driverPin' }).catch(() => {})
            } else if (e && e.code === driverService.BAD_STEP_ORDER) {
                this.error = e.message
                this.showError = true
                await this.load()
            } else {
                this.error = genericMessage
                this.showError = true
            }
        },
        // Page 1 -> 2. Nothing is saved yet: the work photos go up with the
        // customer's page, so a customer who hands the phone back without
        // signing has left nothing half-written.
        toCustomerPage () {
            const problem = P.photoProblem('finished', this.photos.length, this.photoSkippedReason, this.absent)
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            this.finishPage = 2
            // The pad was measured while hidden, at zero width. resize() without
            // force re-measures and keeps any ink, so stepping back to page 1
            // and forward again does not wipe a signature.
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const pad = this.$refs.pad
                    if (pad) pad.resize()
                })
            })
        },
        // Page 2, the customer's บันทึก: their rating and signature, with the
        // driver's work photos. No stamp -- the job closes on page 3.
        async saveCustomerPage () {
            // Same double-tap guard as stamp(): :loading alone does not disable
            // the button, and a second tap would upload the signature twice.
            if (this.busy) return
            const pad = this.$refs.pad
            const hasSignature = pad && !pad.isEmpty()
            const problem = P.customerProblem({
                hasSignature: hasSignature,
                refusedReason: this.refusedReason,
                rating: this.rating,
                ratingDeclined: this.ratingDeclined
            })
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            if (!D.canStamp(this.stop, 'finished')) return this.alreadyClosed()
            this.busy = true
            try {
                // A failed upload keeps the page open with everything still in
                // it, so a retry needs no re-taking and no re-signing. Fixed
                // file names make that retry overwrite, not add.
                const photoUrls = await this.uploadWithNote('finished', this.photos)
                let signatureUrl = ''
                if (hasSignature) {
                    signatureUrl = await driverService.uploadSignature(this.stop, pad.toDataUrl())
                }
                const proof = {
                    signatureUrl: signatureUrl,
                    signedName: this.signedName,
                    rating: this.rating,
                    ratingDeclined: this.ratingDeclined,
                    comment: this.comment,
                    refusedReason: hasSignature ? '' : this.refusedReason,
                    photoUrls: photoUrls,
                    photoSkippedReason: photoUrls.length ? '' : this.photoSkippedReason
                }
                await driverService.saveCustomerProof(this.stop, proof)
                // Locally, so a reopened sheet starts on page 3 even before a
                // reload has fetched the server's copy.
                this.$set(this.stop, 'proof', Object.assign({}, this.stop.proof, proof, { capturedAt: true }))
                this.finishPage = 3
            } catch (e) {
                if (e && e.code === driverService.NOT_SIGNED_IN) {
                    driverService.disarmPing()
                    this.$router.push({ name: 'driverPin' }).catch(() => {})
                } else {
                    this.error = 'บันทึกรูปหรือลายเซ็นไม่สำเร็จ กรุณาลองใหม่ หรือเลือกเหตุผล "ระบบขัดข้อง"'
                    this.showError = true
                }
            }
            this.busy = false
        },
        // Page 3, the driver's: the signed ใบส่งของ and any follow-up, saved
        // before the เสร็จงาน stamp so the stamp never exists without them.
        async closeJob () {
            if (this.busy) return
            const followUp = { followUp: this.followUp, reasons: this.followUpReasons, note: this.followUpNote }
            const problem = P.photoProblem('receipt', this.receiptPhotos.length, this.receiptSkippedReason, this.absent) ||
                O.followUpProblem(followUp)
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            if (!D.canStamp(this.stop, 'finished')) return this.alreadyClosed()
            this.busy = true
            try {
                const receiptPhotoUrls = await this.uploadWithNote('receipt', this.receiptPhotos)
                const fields = await driverService.saveCloseout(this.stop, Object.assign({
                    receiptPhotoUrls: receiptPhotoUrls,
                    receiptSkippedReason: receiptPhotoUrls.length ? '' : this.receiptSkippedReason
                }, followUp))
                const coords = await this.readCoords()
                await driverService.stampStep(this.stop, 'finished', coords, fields)
                // Same reason as stamp(): apply the stamp locally before the
                // reload, not after, so a failed reload can never leave `next`
                // pointing at เสร็จงาน again.
                this.applyLocalStamp('finished')
                driverService.disarmPing()
                this.finishOpen = false
                await this.load()
                // Not awaited and silent: SAP must not hold the driver up, and
                // what fails stays owed for the next day load.
                pushDeliveredToSap(this.$store.getters.webPath, this.stop)
            } catch (e) {
                await this.closeFailed(e, 'บันทึกรูปไม่สำเร็จ กรุณาลองใหม่ หรือเลือกเหตุผล "ระบบขัดข้อง"')
            }
            this.busy = false
        },
        // ส่งไม่สำเร็จ: reasons and evidence first, then the `rejected` stamp.
        async saveReject () {
            if (this.busy) return
            const input = {
                reasons: this.rejectReasons,
                note: this.rejectNote,
                photoCount: this.rejectPhotos.length,
                photoSkippedReason: this.rejectSkippedReason
            }
            const problem = O.rejectProblem(input)
            if (problem) {
                this.error = problem
                this.showError = true
                return
            }
            // Refused before uploading, as in stampWithPhotos: another phone may
            // already have closed this stop.
            if (!D.canStamp(this.stop, D.REJECTED)) return this.alreadyClosed()
            this.busy = true
            try {
                const photoUrls = await this.uploadWithNote(D.REJECTED, this.rejectPhotos)
                const fields = await driverService.saveRejection(this.stop, Object.assign({}, input, {
                    photoUrls: photoUrls,
                    photoSkippedReason: photoUrls.length ? '' : this.rejectSkippedReason
                }))
                const coords = await this.readCoords()
                await driverService.stampStep(this.stop, D.REJECTED, coords, fields)
                this.applyLocalStamp(D.REJECTED)
                driverService.disarmPing()
                this.rejectOpen = false
                this.finishOpen = false
                await this.load()
            } catch (e) {
                await this.closeFailed(e, 'บันทึกไม่สำเร็จ กรุณาลองใหม่ หรือเลือกเหตุผล "ระบบขัดข้อง"')
            }
            this.busy = false
        },
        // Another device closed this stop -- finished or rejected it. Telling
        // the driver to retry would be the one instruction guaranteed not to
        // help, so close the sheets and reload, which shows what happened.
        async alreadyClosed () {
            this.error = 'จุดนี้ถูกปิดงานแล้วจากอุปกรณ์อื่น'
            this.showError = true
            this.finishOpen = false
            this.rejectOpen = false
            await this.load()
        },
        async closeFailed (e, genericMessage) {
            if (e && e.code === driverService.NOT_SIGNED_IN) {
                // As in stamp(): nobody to close the stop for. Back to the
                // keypad, with the ping timer released the same way.
                driverService.disarmPing()
                this.$router.push({ name: 'driverPin' }).catch(() => {})
            } else if (e && e.code === driverService.BAD_STEP_ORDER) {
                await this.alreadyClosed()
            } else {
                this.error = genericMessage
                this.showError = true
            }
        },
        async load () {
            const today = driverService.todayISO()
            this.plate = driverService.readChosenPlate()
            if (!this.plate) {
                this.$router.push({ name: 'driverCar' }).catch(() => {})
                return
            }
            try {
                const stops = await driverService.loadDay(this.plate, today)
                // Idempotent: deterministic ids plus merge, and no step fields are
                // written, so this can run on every load without risk. A driver
                // can land on this route directly on a cold PWA launch -- the
                // installed app's start_url plus route restoration mean day.vue's
                // own ensureStops call is not guaranteed to have run first -- and
                // stampStep's batch.update() REJECTS against a stop document that
                // does not exist yet.
                await driverService.ensureStops(stops)
                this.stop = stops.filter(s => s.id === this.id)[0] || null
                if (!this.stop) this.$router.push({ name: 'driverDay' }).catch(() => {})
                // Arm-only. A reload of THIS screen mid-leg must resume reporting
                // position, but never disarm here: a driver merely reading a
                // different, pending stop must not kill the pinging for the stop
                // they are actually driving to -- only arrived/finished (above) and
                // day.vue's own re-arm (which sees the whole day) get to do that.
                if (this.stop && this.stop.status === 'enroute') driverService.armPing(this.stop)
            } catch (e) {
                if (e && e.code === driverService.NOT_SIGNED_IN) {
                    driverService.disarmPing()
                    this.$router.push({ name: 'driverPin' }).catch(() => {})
                } else {
                    this.error = 'โหลดงานไม่สำเร็จ กรุณาลองใหม่'
                    this.showError = true
                }
            }
        }
    },
    mounted () { this.load() },
    beforeDestroy () {
        if (this.$refs.photos) this.$refs.photos.release()
        if (this.$refs.stagePhotos) this.$refs.stagePhotos.release()
        if (this.$refs.addPhotos) this.$refs.addPhotos.release()
        if (this.$refs.receiptPhotos) this.$refs.receiptPhotos.release()
        if (this.$refs.rejectPhotos) this.$refs.rejectPhotos.release()
    }
}
</script>
